import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Container,
  InputAdornment,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DesignServicesOutlinedIcon from '@mui/icons-material/DesignServicesOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import SubscriptionsOutlinedIcon from '@mui/icons-material/SubscriptionsOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { useAdminData } from '../../contexts/AdminDataContext';
import {
  getCurrentBusinessConfig,
  getStripeAccount,
  patchFeaturesConfig,
  patchPreferencesConfig,
} from '../../firebase/firebase-delegate';
import { useTenant } from '../../tenancy/TenantContext';
import { BusinessConfig } from '../../types';
import { resolveAdminBookingUxConfig } from '../../utils/bookingUxConfig';
import { createControlPlaneTenantUrl } from '../../tenancy/tenantUrls';
import {
  isValidPaymentDeposit,
  MAX_DEPOSIT_PERCENT,
  MIN_CHARGE_CENTS,
  PaymentDeposit,
  PaymentDepositType,
  resolvePaymentDeposit,
} from '../../booking/deposit';

type TenantSettingsDraft = {
  enableCart: boolean;
  enablePayments: boolean;
  requirePaymentForBooking: boolean;
  paymentDeposit: PaymentDeposit;
  allowTips: boolean;
  allowAnyAvailableEmployee: boolean;
  showEmployeePickerInBooking: boolean;
  minimumBookingNoticeHours: number;
  enableCustomerSMS: boolean;
  enableEmployeeSMS: boolean;
  enableEmployeeEmails: boolean;
};

type SettingUpdate = {
  [Key in keyof TenantSettingsDraft]: {
    key: Key;
    value: TenantSettingsDraft[Key];
  }
}[keyof TenantSettingsDraft];

const settingsFromConfig = (config?: BusinessConfig | null): TenantSettingsDraft => {
  const resolved = resolveAdminBookingUxConfig(config);
  return {
    enableCart: config?.features?.enableCart ?? false,
    enablePayments: config?.features?.enablePayments ?? false,
    requirePaymentForBooking: config?.preferences?.requirePaymentForBooking ?? false,
    paymentDeposit: resolvePaymentDeposit(config?.preferences?.paymentDeposit),
    allowTips: config?.preferences?.allowTips ?? false,
    allowAnyAvailableEmployee: config?.preferences?.allowAnyAvailableEmployee ?? false,
    showEmployeePickerInBooking: resolved.uiFlags.showEmployeePickerInBooking,
    minimumBookingNoticeHours: config?.preferences?.minimumBookingNoticeHours ?? 1,
    enableCustomerSMS: config?.features?.enableCustomerSMS ?? false,
    enableEmployeeSMS: config?.features?.enableEmployeeSMS ?? false,
    enableEmployeeEmails: config?.preferences?.enableEmployeeEmails ?? false,
  };
};

const SettingsToggle: React.FC<{
  label: string;
  detail?: string;
  supportingContent?: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
  isSaving?: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, detail, supportingContent, checked, disabled, isSaving, onChange }) => (
  <Box
    sx={{
      py: 1.25,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-child': { borderBottom: 0 },
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={700}>{label}</Typography>
      {detail ? <Typography variant="caption" color="text.secondary">{detail}</Typography> : null}
      {supportingContent}
    </Box>
    {isSaving ? <CircularProgress size={24} aria-label={`Saving ${label}`} /> : (
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        inputProps={{ 'aria-label': label }}
      />
    )}
  </Box>
);

const SettingsCard: React.FC<{
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <Card variant="outlined" sx={{ height: '100%', borderRadius: 3.5 }}>
    <CardContent sx={{ p: { xs: 2.25, sm: 2.75 }, '&:last-child': { pb: { xs: 2.25, sm: 2.75 } } }}>
      <Typography variant="h6" fontWeight={850}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 1.5 }}>
        {description}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

const ActionCard: React.FC<{
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}> = ({ label, icon, onClick }) => (
  <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
    <CardActionArea
      onClick={onClick}
      sx={{ height: '100%', p: 1.75, display: 'flex', alignItems: 'center', gap: 1.5 }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          borderRadius: 2.25,
          color: 'primary.main',
          bgcolor: 'action.hover',
        }}
      >
        {icon}
      </Box>
      <Typography variant="body2" fontWeight={750} sx={{ flex: 1, textAlign: 'left' }}>{label}</Typography>
      <ChevronRightIcon color="disabled" fontSize="small" />
    </CardActionArea>
  </Card>
);

const depositInputFor = (deposit: PaymentDeposit): string => {
  if (deposit.type === 'percent') return String(deposit.percent ?? '');
  if (deposit.type === 'fixed') return deposit.fixedAmountCents ? (deposit.fixedAmountCents / 100).toFixed(2) : '';
  return '';
};

const depositFromInput = (type: PaymentDepositType, input: string): PaymentDeposit | null => {
  if (type === 'full') return { type: 'full' };
  const value = Number(input);
  if (!input.trim() || !Number.isFinite(value)) return null;
  const deposit: PaymentDeposit = type === 'percent'
    ? { type: 'percent', percent: value }
    : { type: 'fixed', fixedAmountCents: Math.round(value * 100) };
  return isValidPaymentDeposit(deposit) ? deposit : null;
};

// How much of a required payment is charged when the customer books.
const DepositSetting: React.FC<{
  value: PaymentDeposit;
  disabled: boolean;
  isSaving: boolean;
  onSave: (deposit: PaymentDeposit) => void;
}> = ({ value, disabled, isSaving, onSave }) => {
  const [type, setType] = useState<PaymentDepositType>(value.type);
  const [input, setInput] = useState(depositInputFor(value));
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setType(value.type);
    setInput(depositInputFor(value));
    setTouched(false);
  }, [value]);

  const candidate = depositFromInput(type, input);
  const showError = touched && type !== 'full' && !candidate;
  const save = () => {
    setTouched(true);
    if (candidate && JSON.stringify(candidate) !== JSON.stringify(value)) onSave(candidate);
  };

  return (
    <Box sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700}>Amount due at booking</Typography>
          <Typography variant="caption" color="text.secondary">
            The rest is paid at the visit. Customers can still choose to pay in full.
          </Typography>
        </Box>
        {isSaving ? <CircularProgress size={24} aria-label="Saving amount due at booking" /> : null}
      </Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={type}
        disabled={disabled || isSaving}
        onChange={(_, next: PaymentDepositType | null) => {
          if (!next) return;
          setType(next);
          setTouched(false);
          if (next === 'full') {
            setInput('');
            if (value.type !== 'full') onSave({ type: 'full' });
          } else {
            setInput(next === value.type ? depositInputFor(value) : '');
          }
        }}
        aria-label="Amount due at booking"
        sx={{ mt: 1, width: '100%', '& .MuiToggleButton-root': { flex: 1, textTransform: 'none', fontWeight: 700 } }}
      >
        <ToggleButton value="full">Full price</ToggleButton>
        <ToggleButton value="percent">Percentage</ToggleButton>
        <ToggleButton value="fixed">Fixed amount</ToggleButton>
      </ToggleButtonGroup>
      {type !== 'full' ? (
        <Box sx={{ mt: 1.25, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            type="number"
            value={input}
            disabled={disabled || isSaving}
            onChange={(event) => setInput(event.target.value)}
            onBlur={save}
            onKeyDown={(event) => { if (event.key === 'Enter') save(); }}
            error={showError}
            helperText={showError
              ? type === 'percent'
                ? `Enter a whole number from 1 to ${MAX_DEPOSIT_PERCENT}.`
                : `Enter at least $${(MIN_CHARGE_CENTS / 100).toFixed(2)}.`
              : type === 'percent'
                ? 'Percent of the booking total.'
                : 'Capped at the booking total. Also charged for "Price varies" services.'}
            inputProps={{
              'aria-label': type === 'percent' ? 'Deposit percentage' : 'Deposit amount',
              min: type === 'percent' ? 1 : MIN_CHARGE_CENTS / 100,
              max: type === 'percent' ? MAX_DEPOSIT_PERCENT : undefined,
              step: type === 'percent' ? 1 : 0.01,
            }}
            InputProps={type === 'percent'
              ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
              : { startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            sx={{ maxWidth: 220 }}
          />
        </Box>
      ) : null}
    </Box>
  );
};

const LEAD_TIME_OPTIONS: Array<{ hours: number; label: string }> = [
  { hours: 0, label: 'Up to the last minute' },
  { hours: 1, label: '1 hour ahead' },
  { hours: 2, label: '2 hours ahead' },
  { hours: 4, label: '4 hours ahead' },
  { hours: 12, label: '12 hours ahead' },
  { hours: 24, label: '1 day ahead' },
  { hours: 48, label: '2 days ahead' },
  { hours: 72, label: '3 days ahead' },
  { hours: 168, label: '1 week ahead' },
];

const leadTimeOptions = (current: number) => LEAD_TIME_OPTIONS.some((option) => option.hours === current)
  ? LEAD_TIME_OPTIONS
  : [...LEAD_TIME_OPTIONS, { hours: current, label: `${current} hours ahead` }].sort((left, right) => left.hours - right.hours);

const SettingsPage: React.FC = () => {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentEmployeeQuery: employeeQuery } = useAdminData();
  const [draft, setDraft] = useState<TenantSettingsDraft>(() => settingsFromConfig());
  const [pendingFields, setPendingFields] = useState<Set<keyof TenantSettingsDraft>>(new Set());
  const pendingFieldsRef = useRef<Set<keyof TenantSettingsDraft>>(new Set());
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const businessConfigQuery = useQuery<BusinessConfig>({
    queryKey: ['businessConfig', tenantId],
    queryFn: () => getCurrentBusinessConfig(tenantId),
  });
  const employeeStripeQuery = useQuery({
    queryKey: ['currentEmployeeStripe', tenantId, employeeQuery.data?.id],
    enabled: Boolean(employeeQuery.data?.id),
    queryFn: () => getStripeAccount(employeeQuery.data!.id, tenantId),
    staleTime: 0,
  });

  useEffect(() => {
    if (businessConfigQuery.data) {
      setDraft(settingsFromConfig(businessConfigQuery.data));
    }
  }, [businessConfigQuery.data]);

  const isAdmin = employeeQuery.data?.admin === true;
  const isOwner = businessConfigQuery.data?.paymentInfo?.owner === employeeQuery.data?.id;
  const bookingUxConfig = resolveAdminBookingUxConfig(businessConfigQuery.data);
  const isResourceMode = bookingUxConfig.bookingModel === 'resource';
  const resourceSingular = bookingUxConfig.terminology.resourceSingular;
  const resourcePlural = bookingUxConfig.terminology.resourcePlural;
  const useBusinessStripe = businessConfigQuery.data?.payoutInfo?.useBusinessStripeAccount === true;
  const showPaymentPortal = draft.enablePayments && (isOwner || !useBusinessStripe);
  const paymentDestinationReady = useBusinessStripe
    ? Boolean(businessConfigQuery.data?.payoutInfo?.businessStripeAccountId)
    : Boolean(employeeStripeQuery.data?.stripe_account_id);

  const saveMutation = useMutation<SettingUpdate, Error, SettingUpdate>({
    mutationFn: async (input) => {
      const { key, value } = input;
      if (key === 'paymentDeposit' && !isValidPaymentDeposit(value)) {
        throw new Error('Enter a valid deposit amount.');
      }
      if (key === 'minimumBookingNoticeHours' &&
          (!Number.isInteger(value) || Number(value) < 0)) {
        throw new Error('Minimum notice must be a non-negative whole number.');
      }
      if (key === 'enablePayments') {
        if (value === false) {
          await Promise.all([
            patchFeaturesConfig({ enablePayments: false }, tenantId),
            patchPreferencesConfig({ requirePaymentForBooking: false, allowTips: false }, tenantId),
          ]);
        } else {
          await patchFeaturesConfig({ enablePayments: true }, tenantId);
        }
      } else if (key === 'enableCustomerSMS' || key === 'enableEmployeeSMS') {
        await patchFeaturesConfig({ [key]: value }, tenantId);
      } else if (key === 'showEmployeePickerInBooking') {
        await patchPreferencesConfig({
          uiFlags: {
            ...(businessConfigQuery.data?.preferences?.uiFlags ?? {}),
            showEmployeePickerInBooking: Boolean(value),
          },
        }, tenantId);
      } else {
        await patchPreferencesConfig({ [key]: value }, tenantId);
      }
      return input;
    },
    onMutate: ({ key }) => {
      pendingFieldsRef.current.add(key);
      setPendingFields(new Set(pendingFieldsRef.current));
    },
    onSuccess: ({ key, value }) => {
      setDraft((current) => ({
        ...current,
        [key]: value,
        ...(key === 'enablePayments' && value === false
          ? { requirePaymentForBooking: false, allowTips: false }
          : {}),
      }));
      queryClient.invalidateQueries({ queryKey: ['businessConfig', tenantId], exact: true });
    },
    onError: (error: Error, { key }) => {
      setSnackbar({ open: true, message: error.message || 'Could not save setting.', severity: 'error' });
    },
    onSettled: (_data, _error, { key }) => {
      pendingFieldsRef.current.delete(key);
      setPendingFields(new Set(pendingFieldsRef.current));
    },
  });

  const saveSetting = <Key extends keyof TenantSettingsDraft>(key: Key, value: TenantSettingsDraft[Key]) => {
    if (!pendingFieldsRef.current.has(key) && draft[key] !== value) {
      saveMutation.mutate({ key, value } as SettingUpdate);
    }
  };

  if (businessConfigQuery.isLoading || employeeQuery.isLoading || employeeStripeQuery.isLoading) return <LoadingSpinner label="Loading settings" />;
  if (businessConfigQuery.isError || !businessConfigQuery.data) {
    return <Alert severity="error">Settings are unavailable right now.</Alert>;
  }

  const actions = [
    { label: 'Appointments', icon: <EventNoteOutlinedIcon />, path: '/admin/appointments', show: true },
    { label: 'Customers', icon: <PeopleOutlineIcon />, path: '/admin/customers', show: true },
    { label: 'Work hours', icon: <AccessTimeOutlinedIcon />, path: '/admin/edit-work-hours', show: !isResourceMode },
    { label: 'Services', icon: <DesignServicesOutlinedIcon />, path: '/admin/manage-services', show: isAdmin },
    { label: `Manage ${resourcePlural}`, icon: <GroupsOutlinedIcon />, path: '/admin/manage-employees', show: isAdmin },
    { label: 'Site Builder', icon: <LanguageOutlinedIcon />, path: '/admin/site', show: isAdmin },
    { label: 'Payment portal', icon: <AccountBalanceWalletOutlinedIcon />, path: '/admin/payment-portal', show: showPaymentPortal },
    { label: 'My profile', icon: <AccountCircleOutlinedIcon />, path: '/admin/my-profile', show: true },
    { label: 'Subscription', icon: <SubscriptionsOutlinedIcon />, path: '/admin/manage-subscription', show: isAdmin && isOwner },
    { label: 'Support', icon: <SupportAgentOutlinedIcon />, path: '/admin/contact-support', show: true },
  ].filter((action) => action.show);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3.5 } }}>
      <Stack spacing={3}>
        {isAdmin ? (
          <Grid2 container spacing={2.25}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <SettingsCard title="Booking" description="Customer choices and checkout.">
                <SettingsToggle
                  label="Card payments"
                  checked={draft.enablePayments}
                  isSaving={pendingFields.has('enablePayments')}
                  onChange={(checked) => saveSetting('enablePayments', checked)}
                  supportingContent={draft.enablePayments && !paymentDestinationReady ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="warning.main" display="block">
                        Setup required before you can accept card payments.
                      </Typography>
                      <Button
                        component="a"
                        href={createControlPlaneTenantUrl('/admin/payment-portal', tenantId)}
                        variant="contained"
                        color="primary"
                        size="medium"
                        startIcon={<AccountBalanceWalletOutlinedIcon />}
                        sx={{
                          mt: 1,
                          px: 2.25,
                          py: 0.9,
                          borderRadius: 2,
                          fontWeight: 750,
                          textTransform: 'none',
                          boxShadow: 2,
                          '&:hover': { boxShadow: 4 },
                        }}
                      >
                        Open Payment Portal
                      </Button>
                    </Box>
                  ) : undefined}
                />
                <SettingsToggle
                  label="Require payment"
                  detail="Payment is required to book"
                  checked={draft.requirePaymentForBooking}
                  disabled={!draft.enablePayments || pendingFields.has('enablePayments')}
                  isSaving={pendingFields.has('requirePaymentForBooking')}
                  onChange={(checked) => saveSetting('requirePaymentForBooking', checked)}
                />
                {draft.enablePayments && draft.requirePaymentForBooking ? (
                  <DepositSetting
                    value={draft.paymentDeposit}
                    disabled={pendingFields.has('requirePaymentForBooking')}
                    isSaving={pendingFields.has('paymentDeposit')}
                    onSave={(deposit) => saveSetting('paymentDeposit', deposit)}
                  />
                ) : null}
                <SettingsToggle
                  label="Tips"
                  checked={draft.allowTips}
                  disabled={!draft.enablePayments || pendingFields.has('enablePayments')}
                  isSaving={pendingFields.has('allowTips')}
                  onChange={(checked) => saveSetting('allowTips', checked)}
                />
                {!isResourceMode ? <SettingsToggle
                  label="Let customers pick who they see"
                  detail={`Show your ${resourcePlural.toLowerCase()} when customers book.`}
                  checked={draft.showEmployeePickerInBooking}
                  isSaving={pendingFields.has('showEmployeePickerInBooking')}
                  onChange={(checked) => saveSetting('showEmployeePickerInBooking', checked)}
                /> : null}
                {!isResourceMode ? <SettingsToggle
                  label="Offer “Anyone available”"
                  detail="Customers can skip choosing and get whoever is free."
                  checked={draft.allowAnyAvailableEmployee}
                  isSaving={pendingFields.has('allowAnyAvailableEmployee')}
                  onChange={(checked) => saveSetting('allowAnyAvailableEmployee', checked)}
                /> : null}
                <Box sx={{
                  py: 1.5,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 1.25, sm: 2 },
                }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700}>Booking lead time</Typography>
                    <Typography variant="caption" color="text.secondary">How far ahead customers must book.</Typography>
                  </Box>
                  {pendingFields.has('minimumBookingNoticeHours') ? (
                    <CircularProgress size={24} aria-label="Saving booking lead time" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }} />
                  ) : (
                    <TextField
                      select
                      size="small"
                      value={draft.minimumBookingNoticeHours}
                      onChange={(event) => saveSetting('minimumBookingNoticeHours', Number(event.target.value))}
                      inputProps={{ 'aria-label': 'Booking lead time' }}
                      sx={{ minWidth: 190, width: { xs: '100%', sm: 'auto' } }}
                    >
                      {leadTimeOptions(draft.minimumBookingNoticeHours).map((option) => (
                        <MenuItem key={option.hours} value={option.hours}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  )}
                </Box>
              </SettingsCard>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <SettingsCard title="Notifications" description="Who hears about new and changed bookings.">
                <SettingsToggle
                  label="Text customers"
                  detail="Booking confirmations and changes by text message."
                  checked={draft.enableCustomerSMS}
                  isSaving={pendingFields.has('enableCustomerSMS')}
                  onChange={(checked) => saveSetting('enableCustomerSMS', checked)}
                />
                <SettingsToggle
                  label={isResourceMode ? 'Text me about bookings' : `Text ${resourcePlural.toLowerCase()}`}
                  detail={isResourceMode
                    ? 'Sent to the person who manages your bookings.'
                    : `Each ${resourceSingular.toLowerCase()} hears about their own bookings.`}
                  checked={draft.enableEmployeeSMS}
                  isSaving={pendingFields.has('enableEmployeeSMS')}
                  onChange={(checked) => saveSetting('enableEmployeeSMS', checked)}
                />
                <SettingsToggle
                  label={isResourceMode ? 'Email me about bookings' : `Email ${resourcePlural.toLowerCase()}`}
                  detail={isResourceMode
                    ? 'Sent to the person who manages your bookings.'
                    : `Each ${resourceSingular.toLowerCase()} hears about their own bookings.`}
                  checked={draft.enableEmployeeEmails}
                  isSaving={pendingFields.has('enableEmployeeEmails')}
                  onChange={(checked) => saveSetting('enableEmployeeEmails', checked)}
                />
              </SettingsCard>
            </Grid2>


          </Grid2>
        ) : (
          <Alert severity="info">Business settings are managed by an administrator.</Alert>
        )}

        <Box>
          <Typography variant="h6" fontWeight={850} sx={{ mb: 1.5 }}>General</Typography>
          <Grid2 container spacing={1.5}>
            {actions.map((action) => (
              <Grid2 key={action.label} size={{ xs: 12, sm: 6, md: 4 }}>
                <ActionCard label={action.label} icon={action.icon} onClick={() => navigate(action.path)} />
              </Grid2>
            ))}
          </Grid2>
        </Box>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SettingsPage;
